import {PayloadAction, createSlice} from '@reduxjs/toolkit'
import {RootState} from '../../store'
import {IFetchNotificationsParams} from 'src/services/types'
import {SingleValue} from 'react-select'

export type TNotification = {
  id: number
  user_id: number
  title: string
  body: string
  is_read: boolean
  created_by: null | string
  created_at: string
  updated_at: string
  type:
    | 'order_sub_header'
    | 'order_submission'
    | 'negotiation'
    | 'findoc'
    | 'sales-order'
    | 'order'
    | 'sip'
    | 'contract'
  read_only: false
  parent_id: string
  child_id: null | number
  flag_nego: null | string
  customer_id: string | number
  role_flag: string
  header_id: string | number
  user: any
}

export type TPaginationLink = {
  url: null | string
  label: string
  active: boolean
}

export type TPagination = {
  first_page_url: string
  from: number
  last_page: number
  last_page_url: string
  links: TPaginationLink[]
  next_page_url: null | string
  path: string
  per_page: number
  prev_page_url: null | string
  to: number
  total: number
}

export interface IState {
  isLoading: boolean
  isError: string
  unreadNotifications: TNotification[]
  notifications: TNotification[]
  notification: TNotification | null
  pagination: TPagination | null
  amount: number
  searchParams: IFetchNotificationsParams
  notificationType: SingleValue<{value: string; label: string}>
}

const initialState: IState = {
  isLoading: true,
  isError: '',
  unreadNotifications: [],
  notifications: [],
  notification: null,
  pagination: null,
  amount: 0,
  searchParams: {
    body: '',
    title: '',
    per_page: 15,
  },
  notificationType: {value: '', label: 'All Type'},
}

/**
 * Slice
 */
const notifcationSlice = createSlice({
  name: 'notification',
  initialState,
  reducers: {
    setIsLoading: (state, action: PayloadAction<IState['isLoading']>) => ({
      ...state,
      isLoading: action.payload,
    }),
    setIsError: (state, action: PayloadAction<IState['isError']>) => ({
      ...state,
      isError: action.payload,
    }),
    setUnreadNotifications: (state, action: PayloadAction<IState['unreadNotifications']>) => ({
      ...state,
      unreadNotifications: action.payload,
    }),
    setNotifications: (state, action: PayloadAction<IState['notifications']>) => ({
      ...state,
      notifications: action.payload,
    }),
    setNotification: (state, action: PayloadAction<IState['notification']>) => ({
      ...state,
      notification: action.payload,
    }),
    setPagination: (state, action: PayloadAction<IState['pagination']>) => ({
      ...state,
      pagination: action.payload,
    }),
    setAmount: (state, action: PayloadAction<number>) => {
      state.amount = action.payload
    },
    setSearchParams: (state, action: PayloadAction<IState['searchParams']>) => {
      state.searchParams = action.payload
    },
    setNotificationType: (state, action: PayloadAction<IState['notificationType']>) => {
      state.notificationType = action.payload
    },
    resetSearchParams: (state) => {
      state.searchParams = {body: '', title: '', type: '', per_page: 15}
      state.notificationType = {value: '', label: 'All Type'}
    },
  },
})

/**
 * Action
 */
export const {
  setIsLoading,
  setIsError,
  setUnreadNotifications,
  setNotifications,
  setNotification,
  setPagination,
  setAmount,
  setSearchParams,
  setNotificationType,
  resetSearchParams,
} = notifcationSlice.actions

/**
 * Selector
 */
export const notificationSelector = (state: RootState) => state.notificationReducer

/**
 * Reducer
 */
export default notifcationSlice.reducer
